'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SearchCategory =
  | 'all' | 'package' | 'hotel' | 'guide' | 'vehicle' | 'destination' |'place' | 'flight' | 'restaurant' | 'event' | 'heritage' | 'community';

export interface SearchResult {
  id: string;
  type: SearchCategory;
  name: string;
  description?: string;
  location?: string;
  price?: number;
  rating?: number;
  image?: string;
  category?: string;
  duration?: string;
  verified?: boolean;
  is_demo?: boolean;
  relevanceScore?: number;
  slug?: string;
}

export interface SearchOptions {
  query: string;
  category: SearchCategory;
  province?: string;
  priceRange?: string;
  sortBy?: string;
  limit?: number;
  offset?: number;
}

// ─── Alias Map ────────────────────────────────────────────────────────────────

const ALIAS_MAP: Record<string, string[]> = {
  'pokhara lakeside': ['pokhara', 'lakeside', 'phewa', 'fewa'],
  'lakeside': ['pokhara', 'lakeside', 'phewa'],
  'phewa lake': ['pokhara', 'phewa', 'fewa', 'lakeside'],
  'pewa lake': ['pokhara', 'phewa', 'fewa', 'lakeside'],
  'ebc': ['everest', 'base camp', 'khumbu', 'solukhumbu'],
  'everest base camp': ['everest', 'base camp', 'khumbu', 'solukhumbu', 'ebc'],
  'ktm': ['kathmandu', 'valley', 'bagmati'],
  'kathmandu': ['kathmandu', 'ktm', 'valley', 'bagmati'],
  'kathmandu valley': ['kathmandu', 'ktm', 'valley', 'bagmati'],
  'annapurna': ['annapurna', 'abc', 'poon hill', 'ghorepani'],
  'abc': ['annapurna', 'base camp', 'poon hill'],
  'mustang': ['mustang', 'lo manthang', 'upper mustang'],
  'chitwan': ['chitwan', 'sauraha', 'jungle', 'safari'],
  'langtang': ['langtang', 'helambu', 'gosaikunda'],
  'rara': ['rara', 'rara lake', 'karnali'],
  'hotels in pokhara': ['hotel', 'pokhara', 'lakeside'],
  'hotels in kathmandu': ['hotel', 'kathmandu', 'thamel'],
  'vehicles from kathmandu to pokhara': ['vehicle', 'kathmandu', 'pokhara'],
  'bus kathmandu pokhara': ['bus', 'kathmandu', 'pokhara'],
  'thamel': ['kathmandu', 'thamel', 'hotel', 'restaurant'],
  'pashupatinath': ['kathmandu', 'temple', 'heritage', 'pashupatinath'],
  'boudhanath': ['kathmandu', 'stupa', 'heritage', 'boudhanath'],
  'swayambhunath': ['kathmandu', 'monkey temple', 'heritage'],
};

// ─── Query Expansion ─────────────────────────────────────────────────────────

function expandQuery(rawQuery: string): string[] {
  const normalized = rawQuery.trim().toLowerCase().replace(/\s+/g, ' ');
  const terms = new Set<string>();
  terms.add(normalized);

  if (ALIAS_MAP[normalized]) {
    ALIAS_MAP[normalized].forEach(t => terms.add(t));
  }

  const words = normalized.split(' ').filter(w => w.length > 1);
  words.forEach(w => terms.add(w));
  words.forEach(w => {
    if (ALIAS_MAP[w]) ALIAS_MAP[w].forEach(t => terms.add(t));
  });

  // Check multi-word aliases
  for (const [alias, expansions] of Object.entries(ALIAS_MAP)) {
    if (normalized.includes(alias)) {
      expansions.forEach(t => terms.add(t));
    }
  }

  return Array.from(terms).filter(t => t.length > 0);
}

function buildOrFilter(columns: string[], terms: string[]): string {
  const conditions: string[] = [];
  for (const col of columns) {
    for (const term of terms) {
      const safe = term.replace(/%/g, '\\%').replace(/_/g, '\\_');
      conditions.push(`${col}.ilike.%${safe}%`);
    }
  }
  return conditions.join(',');
}

// ─── Individual Table Searchers ───────────────────────────────────────────────

async function searchPackages(supabase: any, terms: string[], limit: number): Promise<SearchResult[]> {
  try {
    const filter = buildOrFilter(['name', 'description', 'region', 'category', 'tag', 'destination'], terms);
    const { data, error } = await supabase
      .from('packages')
      .select('id, name, slug, description, region, price, duration, image_url, rating, category, tag')
      .or(filter)
      .limit(limit);
    if (error) { console.warn('[Search] packages error:', error.message); return []; }
    return (data || []).map((p: any) => ({
      id: p.id, type: 'package' as const, name: p.name, description: p.description,
      location: p.region, price: Number(p.price) || undefined, rating: Number(p.rating) || undefined,
      image: p.image_url, duration: p.duration, category: p.category || p.tag, slug: p.slug,
    }));
  } catch { return []; }
}

async function searchHotels(supabase: any, terms: string[], limit: number): Promise<SearchResult[]> {
  try {
    const filter = buildOrFilter(['name', 'location', 'province', 'description', 'city'], terms);
    const { data, error } = await supabase
      .from('hotels')
      .select('id, name, location, province, description, image_url, customer_rating, price_per_night')
      .or(filter)
      .limit(limit);
    if (error) { console.warn('[Search] hotels error:', error.message); return []; }
    return (data || []).map((h: any) => ({
      id: h.id, type: 'hotel' as const, name: h.name, description: h.description,
      location: [h.location, h.province].filter(Boolean).join(', ') || undefined,
      price: Number(h.price_per_night) || undefined, rating: Number(h.customer_rating) || undefined,
      image: h.image_url,
    }));
  } catch { return []; }
}

async function searchGuides(supabase: any, terms: string[], limit: number): Promise<SearchResult[]> {
  try {
    const locationFilter = buildOrFilter(['location', 'province', 'bio', 'category'], terms);
    const { data, error } = await supabase
      .from('guides')
      .select('id, user_id, location, province, daily_rate, average_rating, verified, bio, category, users!guides_user_id_fkey(full_name, profile_photo)')
      .or(locationFilter)
      .limit(limit);
    if (error) { console.warn('[Search] guides error:', error.message); return []; }

    const nameFilter = buildOrFilter(['full_name'], terms);
    let nameData: any[] = [];
    try {
      const nameRes = await supabase.from('users').select('id, full_name, profile_photo').or(nameFilter).eq('role', 'guide').limit(limit);
      if (!nameRes.error && nameRes.data) {
        const userIds = nameRes.data.map((u: any) => u.id);
        if (userIds.length > 0) {
          const guideRes = await supabase.from('guides').select('id, user_id, location, province, daily_rate, average_rating, verified, bio, category, users!guides_user_id_fkey(full_name, profile_photo)').in('user_id', userIds).limit(limit);
          if (!guideRes.error && guideRes.data) nameData = guideRes.data;
        }
      }
    } catch {}

    const allGuides = [...(data || []), ...nameData];
    const seen = new Set<string>();
    const unique = allGuides.filter(g => { if (seen.has(g.id)) return false; seen.add(g.id); return true; });
    return unique.map((g: any) => ({
      id: g.id, type: 'guide' as const, name: (g.users as any)?.full_name || 'Guide',
      description: g.bio, location: [g.location, g.province].filter(Boolean).join(', ') || undefined,
      price: Number(g.daily_rate) || undefined, rating: Number(g.average_rating) || undefined,
      image: (g.users as any)?.profile_photo, verified: g.verified === 'verified',
    }));
  } catch { return []; }
}

async function searchVehicles(supabase: any, terms: string[], limit: number): Promise<SearchResult[]> {
  try {
    const filter = buildOrFilter(['vehicle_type', 'model', 'pickup_location', 'location', 'description'], terms);
    const { data, error } = await supabase
      .from('vehicles')
      .select('id, vehicle_type, model, pickup_location, location, price_per_day, image_url, rating, description')
      .or(filter)
      .eq('is_available', true)
      .limit(limit);
    if (error) { console.warn('[Search] vehicles error:', error.message); return []; }
    return (data || []).map((v: any) => ({
      id: v.id, type: 'vehicle' as const,
      name: [v.vehicle_type, v.model].filter(Boolean).join(' ') || 'Vehicle',
      description: v.description,
      location: v.location || v.pickup_location,
      price: Number(v.price_per_day) || undefined, rating: Number(v.rating) || undefined,
      image: v.image_url,
    }));
  } catch { return []; }
}

async function searchDestinations(supabase: any, terms: string[], limit: number): Promise<SearchResult[]> {
  try {
    const filter = buildOrFilter(['name', 'region', 'province', 'description', 'tag', 'slug'], terms);
    const { data, error } = await supabase
      .from('destinations')
      .select('id, name, slug, region, province, description, image_url, tag')
      .or(filter)
      .limit(limit);
    if (error) { console.warn('[Search] destinations error:', error.message); return []; }
    return (data || []).map((d: any) => ({
      id: d.id, type: 'destination' as const, name: d.name,
      description: d.description, location: [d.region, d.province].filter(Boolean).join(', ') || undefined,
      image: d.image_url, category: d.tag, slug: d.slug,
    }));
  } catch { return []; }
}

async function searchPlaces(supabase: any, terms: string[], limit: number): Promise<SearchResult[]> {
  try {
    const filter = buildOrFilter(['title', 'description', 'district', 'province', 'municipality', 'nearest_landmark', 'category'], terms);
    const { data, error } = await supabase
      .from('discovered_places')
      .select('id, title, description, category, cover_photo_url, district, province, estimated_cost_npr')
      .eq('status', 'published')
      .or(filter)
      .limit(limit);
    if (error) { console.warn('[Search] places error:', error.message); return []; }
    return (data || []).map((p: any) => ({
      id: p.id, type: 'place' as const, name: p.title, description: p.description,
      location: [p.district, p.province].filter(Boolean).join(', ') || undefined,
      price: p.estimated_cost_npr || undefined, image: p.cover_photo_url, category: p.category,
    }));
  } catch { return []; }
}

async function searchCommunityPlaces(supabase: any, terms: string[], limit: number): Promise<SearchResult[]> {
  try {
    const filter = buildOrFilter(['name', 'description', 'location', 'category', 'tags'], terms);
    const { data, error } = await supabase
      .from('community_places')
      .select('id, name, description, location, category, image_url, rating, tags')
      .or(filter)
      .limit(limit);
    if (error) { console.warn('[Search] community_places error:', error.message); return []; }
    return (data || []).map((p: any) => ({
      id: p.id, type: 'community' as const, name: p.name, description: p.description,
      location: p.location, rating: Number(p.rating) || undefined, image: p.image_url, category: p.category,
    }));
  } catch { return []; }
}

// ─── Price Filter ─────────────────────────────────────────────────────────────

function applyPriceFilter(results: SearchResult[], priceRange: string): SearchResult[] {
  if (!priceRange || priceRange === 'all') return results;
  if (priceRange.endsWith('+')) {
    const min = parseInt(priceRange);
    return results.filter(r => r.price == null || r.price >= min);
  }
  const [min, max] = priceRange.split('-').map(Number);
  return results.filter(r => r.price == null || (r.price >= min && r.price <= max));
}

function applyProvinceFilter(results: SearchResult[], province: string): SearchResult[] {
  if (!province || province === 'All Nepal') return results;
  const p = province.toLowerCase();
  return results.filter(r => !r.location || r.location.toLowerCase().includes(p));
}

// ─── Relevance Scoring ────────────────────────────────────────────────────────

function scoreResult(result: SearchResult, originalQuery: string): number {
  const q = originalQuery.toLowerCase();
  const name = (result.name || '').toLowerCase();
  const loc = (result.location || '').toLowerCase();
  const desc = (result.description || '').toLowerCase();

  let score = 0;
  if (name === q) score += 100;
  else if (name.startsWith(q)) score += 80;
  else if (name.includes(q)) score += 60;
  else if (loc.includes(q)) score += 50;
  else {
    const words = q.split(' ').filter(Boolean);
    const allInName = words.every(w => name.includes(w));
    const allInLoc = words.every(w => loc.includes(w));
    if (allInName) score += 40;
    else if (allInLoc) score += 30;
    else {
      const matchCount = words.filter(w => name.includes(w) || loc.includes(w) || desc.includes(w)).length;
      score += matchCount * 10;
    }
  }
  if (result.verified) score += 5;
  if (result.rating && result.rating >= 4.5) score += 3;
  return score;
}

// ─── Main Search Function ─────────────────────────────────────────────────────

export async function searchAllContent(options: SearchOptions): Promise<SearchResult[]> {
  const { query, category, province = 'All Nepal', priceRange = 'all', sortBy = 'relevance', limit = 20 } = options;
  const supabase = createClient();
  const trimmedQuery = query.trim();
  const terms = trimmedQuery ? expandQuery(trimmedQuery) : [];
  const shouldSearch = (type: SearchCategory) => category === 'all' || category === type;

  if (!trimmedQuery) return [];

  const searches: Promise<SearchResult[]>[] = [];
  if (shouldSearch('package')) searches.push(searchPackages(supabase, terms, limit));
  if (shouldSearch('hotel')) searches.push(searchHotels(supabase, terms, limit));
  if (shouldSearch('guide')) searches.push(searchGuides(supabase, terms, limit));
  if (shouldSearch('vehicle')) searches.push(searchVehicles(supabase, terms, limit));
  if (shouldSearch('destination')) searches.push(searchDestinations(supabase, terms, limit));
  if (shouldSearch('place')) searches.push(searchPlaces(supabase, terms, limit));
  if (shouldSearch('community')) searches.push(searchCommunityPlaces(supabase, terms, limit));

  const settled = await Promise.allSettled(searches);
  let allResults: SearchResult[] = [];
  settled.forEach(result => {
    if (result.status === 'fulfilled') allResults.push(...result.value);
  });

  const seen = new Set<string>();
  allResults = allResults.filter(r => {
    const key = `${r.type}-${r.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  allResults = applyProvinceFilter(allResults, province);
  allResults = applyPriceFilter(allResults, priceRange);

  if (trimmedQuery) {
    allResults = allResults.map(r => ({ ...r, relevanceScore: scoreResult(r, trimmedQuery) }));
  }

  if (sortBy === 'relevance') allResults.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));
  else if (sortBy === 'price_low') allResults.sort((a, b) => (a.price || 0) - (b.price || 0));
  else if (sortBy === 'price_high') allResults.sort((a, b) => (b.price || 0) - (a.price || 0));
  else if (sortBy === 'rating') allResults.sort((a, b) => (b.rating || 0) - (a.rating || 0));

  return allResults;
}

// ─── Featured Content ─────────────────────────────────────────────────────────

export async function getFeaturedContent(): Promise<{ featured: SearchResult[]; trending: SearchResult[] }> {
  const supabase = createClient();
  try {
    const [pkgResult, destResult, hotelResult, placeResult] = await Promise.allSettled([
      supabase.from('packages').select('id, name, slug, description, region, price, duration, image_url, rating').order('created_at', { ascending: false }).limit(6),
      supabase.from('destinations').select('id, name, slug, region, province, image_url, tag').eq('is_featured', true).limit(4),
      supabase.from('hotels').select('id, name, location, province, image_url, customer_rating, price_per_night').eq('is_featured', true).limit(4),
      supabase.from('discovered_places').select('id, title, category, cover_photo_url, district, province').eq('status', 'published').order('created_at', { ascending: false }).limit(4),
    ]);

    const pkgs = pkgResult.status === 'fulfilled' ? (pkgResult.value.data || []) : [];
    const dests = destResult.status === 'fulfilled' ? (destResult.value.data || []) : [];
    const hotels = hotelResult.status === 'fulfilled' ? (hotelResult.value.data || []) : [];
    const places = placeResult.status === 'fulfilled' ? (placeResult.value.data || []) : [];

    const featured: SearchResult[] = [
      ...pkgs.slice(0, 3).map((p: any) => ({ id: p.id, type: 'package' as const, name: p.name, description: p.description, location: p.region, price: Number(p.price) || undefined, rating: Number(p.rating) || undefined, image: p.image_url, duration: p.duration, slug: p.slug })),
      ...dests.slice(0, 2).map((d: any) => ({ id: d.id, type: 'destination' as const, name: d.name, location: [d.region, d.province].filter(Boolean).join(', ') || undefined, image: d.image_url, category: d.tag, slug: d.slug })),
      ...hotels.slice(0, 2).map((h: any) => ({ id: h.id, type: 'hotel' as const, name: h.name, location: [h.location, h.province].filter(Boolean).join(', ') || undefined, price: Number(h.price_per_night) || undefined, rating: Number(h.customer_rating) || undefined, image: h.image_url })),
    ];

    const trending: SearchResult[] = [
      ...places.map((p: any) => ({ id: p.id, type: 'place' as const, name: p.title, location: [p.district, p.province].filter(Boolean).join(', ') || undefined, category: p.category, image: p.cover_photo_url })),
      ...pkgs.slice(3).map((p: any) => ({ id: p.id, type: 'package' as const, name: p.name, location: p.region, price: Number(p.price) || undefined, image: p.image_url, slug: p.slug })),
    ];

    return { featured, trending };
  } catch {
    return { featured: [], trending: [] };
  }
}
